import React, { useState, useEffect } from "react"
import "../styles/LeadDetails.css"
import { jsPDF } from "jspdf"
import html2canvas from "html2canvas"
import { useToast } from "../../hooks/useToast"

function LeadDetails({
  lead,
  onSave,
  onCancel,
  isEditable,
  setIsEditable,
  onCreateNew,
}) {
  if (!lead) return <p>No lead selected</p>

  const { notify } = useToast()
  const [localLeadDetails, setLocalLeadDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: "",
    message: "",
    service: "",
    preferredAppointmentDateTime: "",
    initialFollowUp: false,
    jobQuoteCreated: false,
    invoiceSent: false,
    contractSigned: false,
    jobCompleted: false,
    jobPaid: false,
    openBalance: false,
  })

  useEffect(() => {
    if (lead) {
      setLocalLeadDetails({
        ...lead,
        preferredAppointmentDateTime: lead.preferredAppointmentDateTime
          ? new Date(lead.preferredAppointmentDateTime)
              .toISOString()
              .slice(0, 16)
          : "",
      })
    }
  }, [lead])

  const handleEdit = () => {
    setIsEditable(!isEditable)
  }

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target
    setLocalLeadDetails((prevDetails) => ({
      ...prevDetails,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    try {
      onSave(localLeadDetails)
      notify("Lead Details Saved !", "success")
    } catch (error) {
      notify("Error Saving Lead Details.", "error")
    }
  }

  const exportToPDF = async () => {
    const pdf = new jsPDF()
    const leadDetailsElement = document.querySelector(".leadDetails-container")
    const canvas = await html2canvas(leadDetailsElement)
    pdf.addImage(canvas.toDataURL(), "PNG", 0, 0)
    pdf.save("LeadDetails.pdf")
  }

  if (!lead) {
    return (
      <div className="leadDetails-noLead">
        <p>No Lead Selected</p>
        <button onClick={onCreateNew}>Create New Lead</button>
      </div>
    )
  }

  return (
    <div className="leadDetails-container">
      <h2 className="leadDetailsHeading">
        {`Lead Details for ${lead.firstName} ${lead.lastName}`}
      </h2>
      <form className="leadDetailsCard" onSubmit={handleSubmit}>
        <div className="leadDetailsCardBody">
          <div className="leadDetailsCardInputFields">
            <label htmlFor="firstName">
              <strong>First Name:</strong>
              <input
                type="text"
                id="firstName"
                name="firstName"
                placeholder={lead.firstName || ""}
                value={localLeadDetails.firstName}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>
            <label htmlFor="lastName">
              <strong>Last Name:</strong>
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder={lead.lastName || ""}
                value={localLeadDetails.lastName}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>
            <label htmlFor="email">
              <strong>Email:</strong>
              <input
                type="email"
                id="email"
                name="email"
                placeholder={lead.email || ""}
                value={localLeadDetails.email}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>
            <label htmlFor="phone">
              <strong>Phone:</strong>
              <input
                type="text"
                id="phone"
                name="phone"
                placeholder={lead.phone || ""}
                value={localLeadDetails.phone}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>
            <label htmlFor="city">
              <strong>City:</strong>
              <input
                type="text"
                id="city"
                name="city"
                placeholder={lead.city || ""}
                value={localLeadDetails.city}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>
            <label htmlFor="zipCode">
              <strong>Zip Code:</strong>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                placeholder={lead.zipCode || ""}
                value={localLeadDetails.zipCode}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>

            <label htmlFor="message-display">
              <strong>Message from Lead</strong>
              <textarea
                id="message"
                name="message"
                placeholder={lead.message || ""}
                value={localLeadDetails.message}
                onChange={handleChange}
                disabled={!isEditable}
              />
            </label>
          </div>

          <div className="leadDetailsCardSelectables">
            <label htmlFor="service">
              <strong>Service:</strong>
              <select
                id="service"
                name="service"
                value={localLeadDetails.service || ""}
                onChange={handleChange}
              >
                <option value="Gutter Cleaning">Gutter Cleaning</option>
                <option value="Gutter Repairs">Gutter Repairs</option>
                <option value="Gutter Installation">Gutter Installation</option>
                <option value="Gutter Guards Installation">
                  Gutter Guards
                </option>
                <option value="Gutter Replacement">Gutter Replacement</option>
                <option value="Leak Detection & Repairs">
                  Leak Detection & Repairs
                </option>
                <option value="Roof Cleaning">Roof Cleaning</option>
              </select>
            </label>
            <label htmlFor="preferredAppointmentDateTime">
              <strong>Preferred Appointment Date:</strong>
              <input
                type="datetime-local"
                id="preferredAppointmentDateTime"
                name="preferredAppointmentDateTime"
                value={localLeadDetails.preferredAppointmentDateTime || ""}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="initialFollowUp">
              <strong>Initial Follow Up:</strong>
              <input
                type="checkbox"
                id="initialFollowUp"
                name="initialFollowUp"
                checked={localLeadDetails.initialFollowUp || false}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="jobQuoteCreated">
              <strong>Quote Created:</strong>
              <input
                type="checkbox"
                id="quoteCreated"
                name="jobQuoteCreated"
                checked={localLeadDetails.jobQuoteCreated || false}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="contractSigned">
              <strong>Contract Signed:</strong>
              <input
                type="checkbox"
                id="contractSigned"
                name="contractSigned"
                checked={localLeadDetails.contractSigned || false}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="jobCompleted">
              <strong>Job Completed:</strong>
              <input
                type="checkbox"
                id="jobCompleted"
                name="jobCompleted"
                checked={localLeadDetails.jobCompleted || false}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="invoiceSent">
              <strong>Invoice Sent:</strong>
              <input
                type="checkbox"
                id="invoiceSent"
                name="invoiceSent"
                checked={localLeadDetails.invoiceSent || false}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="openBalance">
              <strong>Open Balance:</strong>
              <input
                type="checkbox"
                id="openBalance"
                name="openBalance"
                checked={localLeadDetails.openBalance || false}
                onChange={handleChange}
              />
            </label>
            <label htmlFor="jobPaid">
              <strong>Job Paid:</strong>
              <input
                type="checkbox"
                id="jobPaid"
                name="jobPaid"
                checked={localLeadDetails.jobPaid || false}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className="leadDetailsActions">
            {isEditable ? (
              <button type="submit">Save Changes</button>
            ) : (
              <button type="button" onClick={handleEdit}>
                Edit
              </button>
            )}
            <button type="button" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" onClick={exportToPDF}>
              Export to PDF
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default LeadDetails
